import { useState } from 'react';
import { MapPin, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectLocationsApi } from '../../../api/projectLocations';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';

const LEVEL_LABELS: Record<string, string> = { country: 'Country', state: 'State', lga: 'LGA', ward: 'Ward', community: 'Community' };

/**
 * Structured Select State -> Select LGA flow (Section 11), replacing
 * the old free-text search. An LGA can never be picked before a
 * state is picked - the LGA list is only ever fetched once a state
 * is selected, and it reloads whenever the state changes. Multiple
 * LGAs (even across different states) can be added to the project.
 */
export function LocationSelector({ projectId, canManage }: { projectId: number; canManage: boolean }) {
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const selectedQuery = useQuery({ queryKey: ['project-locations', projectId], queryFn: () => projectLocationsApi.list(projectId) });
  const statesQuery = useQuery({ queryKey: ['location-states'], queryFn: () => projectLocationsApi.states() });
  const lgasQuery = useQuery({
    queryKey: ['location-lgas', selectedStateId],
    queryFn: () => projectLocationsApi.lgasForState(selectedStateId as number),
    enabled: selectedStateId !== null,
  });

  const addMutation = useMutation({
    mutationFn: (locationId: number) => projectLocationsApi.add(projectId, locationId),
    onSuccess: () => { showSuccess('Location added.'); queryClient.invalidateQueries({ queryKey: ['project-locations', projectId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add location.'),
  });

  const removeMutation = useMutation({
    mutationFn: (locationId: number) => projectLocationsApi.remove(projectId, locationId),
    onSuccess: () => { showSuccess('Location removed.'); queryClient.invalidateQueries({ queryKey: ['project-locations', projectId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove location.'),
  });

  const selectedIds = new Set(selectedQuery.data?.data.map((l) => l.location_id) ?? []);

  return (
    <div className="location-selector">
      <div className="selected-locations">
        {selectedQuery.data?.data.length === 0 && <p className="muted">No implementation locations selected yet.</p>}
        {selectedQuery.data?.data.map((loc) => (
          <span key={loc.id} className="location-chip">
            <MapPin size={12} className="inline-icon" />
            {loc.name} <span className="muted">({LEVEL_LABELS[loc.level]})</span>
            {canManage && (
              <button type="button" className="location-chip-remove" onClick={() => removeMutation.mutate(loc.location_id)} aria-label={`Remove ${loc.name}`}>
                <X size={12} />
              </button>
            )}
          </span>
        ))}
      </div>

      {canManage && (
        <div className="location-cascade">
          <label className="location-cascade-field">
            Select State
            <select
              value={selectedStateId ?? ''}
              onChange={(e) => setSelectedStateId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">- choose a state -</option>
              {statesQuery.data?.data.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>

          {selectedStateId !== null && (
            <div className="location-cascade-field">
              <p className="muted location-cascade-label">Select LGA(s)</p>
              {lgasQuery.isLoading && <p className="muted">Loading LGAs…</p>}
              {lgasQuery.data && lgasQuery.data.data.length === 0 && <p className="muted">No LGAs found for this state.</p>}
              <div className="lga-checkbox-grid">
                {lgasQuery.data?.data.map((lga) => {
                  const isSelected = selectedIds.has(lga.id);
                  return (
                    <label key={lga.id} className={`lga-checkbox ${isSelected ? 'lga-checkbox-selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={addMutation.isPending || removeMutation.isPending}
                        onChange={(e) => {
                          if (e.target.checked) addMutation.mutate(lga.id);
                          else removeMutation.mutate(lga.id);
                        }}
                      />
                      {lga.name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
